import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BaseService } from './base.service';
import { HelperService } from './helper.service';
import { Observable, of, map, switchMap, combineLatest } from 'rxjs';
import { Geography } from '../../shared/models/geography.model';
import { API_ENDPOINTS, HYDRA_ENTITY_CODES } from '../../shared/constants/api-endpoints';
import { Coverage } from '../../shared/models/coverage.model';
import { IClaim, ICreateClaimRequest, ICreateClaimResponse, IGetClaimsRequest, IExternalFinancialResponse, IExternalMappingResponse, CompanyResponse, IHydraHierarchyResponse } from '../../shared/interfaces/claims.model';
import type { ICompanySearchRequest, IGetCompanySearch} from '../../shared/interfaces/company-search.interface';
import type { BrandSearchRequest, GetBrandSearch } from '../../shared/interfaces/brand-search.interface';
import { User } from '../../shared/interfaces/user.model';
import { OFFICE_CODE_OPTIONS } from '../../shared/constants/claims-ui-constants';
import { Product } from '../../shared/models/product.model';
import { CatalogService } from './catalog.service';
import { Project } from '../../shared/models/project.mode';
import { ICalculatedBrandCompanyResponse } from '../../shared/interfaces/calculated-brand-company';
import { FavouriteGeography, FavouriteGeographyDto } from '../../shared/models/favourite-geography.response.model';

@Injectable({
    providedIn: 'root'
})
export class ClaimsService extends BaseService {
    constructor(
        public override http: HttpClient,
        private readonly helperService: HelperService,
        private readonly catalogService: CatalogService
    ) {
        super(http);
    }

    public getProjectGeographies(projectCode: string): Observable<Array<Geography>> {
        let url = this.helperService.prepareAPIURLBasedOnRegions();
        url = `${url}${API_ENDPOINTS.sizesUrl}/projects/${projectCode}/geographies`;
        return this.baseGet<Array<Geography>>(url);
    }

    public getProjectProducts(projectCode: string): Observable<Array<Product>> {
            let url = this.helperService.prepareAPIURLBasedOnRegions();
            url = `${url}${API_ENDPOINTS.sharesUrl}/projects/${projectCode}/products`;
            return this.baseGet<Array<Product>>(url);
    }

    public getProjectCoverage(projectCode: string, geographyTypeIds: ReadonlyArray<number>): Observable<Array<Coverage>> {
        let url = this.helperService.prepareAPIURLBasedOnRegions();
        url = `${url}${API_ENDPOINTS.sizesUrl}/projects/${projectCode}/coverage?GeographyTypeIds=${geographyTypeIds}`;
        return this.baseGet<Array<Coverage>>(url);
    }


  public getUsers(searchTerm?: string): Observable<Array<User>> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.authorization}/users`;
    if (searchTerm && searchTerm.length >= 1) {
      url += `?displayName=${encodeURIComponent(searchTerm)}`;
    }
    return this.baseGet<Array<User>>(url);
  }

  public getIOffices(): Observable<Array<{name: string, code: string}>> {
    const offices = OFFICE_CODE_OPTIONS.map((option) => ({
      name: option.label,
      code: option.code
    }));
    
    return of(offices);
  }

  public getLanguages(): Observable<Array<{ id: string; name: string }>> {
    let url = this.helperService.transformMRDAPIURL();
    url = `${url}hierarchies/attributes/${HYDRA_ENTITY_CODES.LANGUAGE_PARENT}?collection=default`;
    return this.baseGet<IHydraHierarchyResponse>(url).pipe(
      map((response) => {
        const children = response?.result?.rootEntities?.[0]?.childEntities ?? [];
        return children
          .map((entity) => ({ id: entity.entityCode, name: entity.entityName }))
          .sort((a, b) => a.name.localeCompare(b.name));
      })
    );
  }

  public createClaim(claimData: ICreateClaimRequest): Observable<ICreateClaimResponse> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.claims}/claims/create`;
    return this.basePost<ICreateClaimResponse>(url, claimData);
  }

  public updateClaim(claimId: number, claimData: any): Observable<ICreateClaimResponse> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.claims}/claims/${claimId}`;
    return this.basePut<ICreateClaimResponse>(url, claimData);
  }

    public getClaims(claimsRequest: IGetClaimsRequest): Observable<Array<IClaim>> {
        let url = this.helperService.prepareAPIURLBasedOnRegions();
        url = `${url}${API_ENDPOINTS.claims}/claims`;
        
        return combineLatest([
            this.basePost<Array<IClaim>>(url, claimsRequest),
            this.catalogService.getAllProjects()
        ]).pipe(
            map(([claims, projects]) => {
                const projectMap = new Map<number, Project>();
                projects.forEach((p: Project) => projectMap.set(p.id, p));
                
                return claims.map(claim => ({
                    ...claim,
                    assertions: claim.assertions?.map(assertion => {
                        const project = projectMap.get(assertion.projectId);
                        return {
                            ...assertion,
                            projectCode: project?.code,
                            projectName: project?.name
                        };
                    })
                }));
            })
        );
    }

  public getCRMLink(sourceTypeId: number): Observable<string> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.companyFinancialUrl}/external-financial-sources?sourceTypeIds=${sourceTypeId}`;

    return this.baseGet<{ data: Array<IExternalFinancialResponse> }>(url).pipe(
      map((response: { data: Array<IExternalFinancialResponse> } | undefined): string => {
        const dataArray: Array<IExternalFinancialResponse> = response?.data ?? [];
        const recordWithLink: IExternalFinancialResponse | undefined = dataArray.find(
          (r: IExternalFinancialResponse) => !!r.linkwithId
        );

        return recordWithLink?.linkwithId ?? '';
      })
    );
  }
 
    public deleteClaims(claimIds: Array<number>): Observable<string> {
        let url = this.helperService.prepareAPIURLBasedOnRegions();
        url = `${url}${API_ENDPOINTS.claims}/claims`;

        let params = new HttpParams();
        claimIds.forEach(id => {
            params = params.append('claimIds', id.toString());
        });

        return this.http.delete(url, { responseType: 'text', params: params });
    }

    public createAssertion(assertionData: any): Observable<string> {
        let url = this.helperService.prepareAPIURLBasedOnRegions();
        url = `${url}${API_ENDPOINTS.claims}/claims/assertions`;
        return this.basePost<any>(url, assertionData);
    }
  
  public deleteAssertions(assertionIds: Array<number>): Observable<string> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.claims}/claims/assertions`;

    let params = new HttpParams();
    assertionIds.forEach((id) => {
      params = params.append('assertionIds', id.toString());
    });

    return this.http.delete(url, { responseType: 'text', params });
  }

  public getCompanyNameByCrmAccountId(crmAccountId: string): Observable<string | null> {
    if (!crmAccountId) {
      return of(null);
    }
    const baseUrl = this.helperService.prepareAPIURLBasedOnRegions();
    const mappingUrl = `${baseUrl}${API_ENDPOINTS.companyFinancialUrl}/external-financial-sources/mapping/get?externalId=${crmAccountId}`;

    return this.http.get<IExternalMappingResponse>(mappingUrl).pipe(
      map(res => res?.data?.[0]?.emiCompanyId ?? null),

      switchMap(emiCompanyId => {
        if (!emiCompanyId) {
          return of(null);
        }
        const companyUrl = `${baseUrl}${API_ENDPOINTS.sharesUrl}/projects/mrd-companies?CompanyIds=${emiCompanyId}`;
       
        return this.http.get<CompanyResponse>(companyUrl).pipe(
          map(res => res?.[0]?.companyName ?? null)
        );
      })
    );
  }

  public getAllCompanies(searchRequest: ICompanySearchRequest): Observable<IGetCompanySearch> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url =
      `${url}${API_ENDPOINTS.sharesUrl}/projects/${searchRequest.projectCode}/search-companies?` +
      `CompanyName=${searchRequest.companyName}` +
      `&CtyCode=${searchRequest.ctyCode}` +
      (searchRequest.companyType ? `&CompanyType=${searchRequest.companyType}` : '') +
      (searchRequest.productCode ? `&ProductCode=${searchRequest.productCode}` : '');

    return this.baseGet<IGetCompanySearch>(url);
  }

  public searchCompanies(
    projectCode: string,
    searchTerm: string,
    companyType?: string | null,
    geographyIds?: ReadonlyArray<string>,
    productCode?: string | null
  ): Observable<CompanyResponse> {
    const baseUrl = this.helperService.prepareAPIURLBasedOnRegions();
    const url = `${baseUrl}${API_ENDPOINTS.sharesUrl}/projects/company-search`;

    let params = new HttpParams().set('projectCode', projectCode).set('searchTerm', searchTerm);

    if (companyType) {
      params = params.set('companyType', companyType);
    }

    if (productCode) {
      params = params.set('productCode', productCode);
    }

    if (geographyIds && geographyIds.length > 0) {
      geographyIds.forEach((id) => {
        params = params.append('geographyIds', String(id));
      });
    }

    return this.http.get<CompanyResponse>(url, { params });
  }

  public searchBrands(request: BrandSearchRequest): Observable<GetBrandSearch> {
    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.sharesUrl}/projects/${request.projectCode}/search-brands`;
    
    let params = new HttpParams()
      .set('BrandName', request.brandName);

    if (request.companyId && String(request.companyId).trim()) {
      params = params.set('CompanyId', String(request.companyId).trim());
    }

    if (request.productCode && request.productCode.trim()) {
      params = params.set('ProductCode', request.productCode.trim());
    }

    if (request.ctyCode && request.ctyCode.trim()) {
      params = params.set('CtyCode', request.ctyCode.trim());
    }

    return this.http.get<GetBrandSearch>(url, { params });
  }

  public getCalculatedBrandAndCompanies(params: {
    projectCode: string;
    shareTypeId: string;
    geographyCodes: Array<string>;
    productIds: Array<string>;
  }): Observable<ICalculatedBrandCompanyResponse> {

    let url = this.helperService.prepareAPIURLBasedOnRegions();
    url = `${url}${API_ENDPOINTS.sharesUrl}/projects/${params.projectCode}/calculated-brands-companies`;

    let httpParams = new HttpParams()
      .set('CalculatedShareType', params.shareTypeId)
      .set('BrandCompanyType', params.shareTypeId);

    params.geographyCodes.forEach(code => {
      httpParams = httpParams.append('EmiGeographyCodes', code);
    });

    params.productIds.forEach(id => {
      httpParams = httpParams.append('ProductIds', id.toString());
    });

    return this.http.get<ICalculatedBrandCompanyResponse>(url, { params: httpParams });
  }

  public getFavouriteGeographies(userId: string): Observable<Array<FavouriteGeography>> {
      let url = this.helperService.prepareAPIURLBasedOnRegions();
      url = `${url}${API_ENDPOINTS.preferenceUrl}/favourite-geographies/${encodeURIComponent(userId)}`;
      return this.baseGet<Array<FavouriteGeographyDto>>(url).pipe(
          map(favourites => favourites.map(favourite => this.mapToFavouriteGeography(favourite)))
      );
  }

  private mapToFavouriteGeography(dto: FavouriteGeographyDto): FavouriteGeography {
      return {
          id: dto.geographyGroupId,
          name: dto.geographyGroupName,
          geographyIds: dto.geographies.map(geography => geography.geographyId)
      };
  }
}